import { useI18n } from '@muse/i18n'

import { LinkifiedText } from '@ds/components'
import { hasClickableLinks } from '@core/linkify'
import { type TCrossLinkTag } from '@core/models'
import { getAuthorPageUrl } from '@core/routes'
import { buildCollectionTagLinks, COLLECTION_TYPE_LABELS } from '@features/collection'
import { FollowButtonContainer, useFollowStatistics } from '@features/follow'

import { useCollectionScreenCollection } from '../store/useCollectionScreenCollection'
import { useCollectionScreenLoaderData } from '../store/useCollectionScreenLoaderData'
import { CollectionHero } from '../components/collection-hero/CollectionHero'
import { CollectionHeroFollow } from '../components/collection-hero/CollectionHeroFollow'
import { CollectionTags } from '../components/collection-hero/CollectionTags'

import { CollectionActionsContainer } from './CollectionActionsContainer'

const NO_CROSS_LINKS: TCrossLinkTag[] = []

export const CollectionHeroContainer = () => {
    const { t, getNumber } = useI18n()
    const collection = useCollectionScreenCollection()
    const followStatistics = useFollowStatistics(collection.authorUsername)
    const { isEditor } = useCollectionScreenLoaderData()

    // Every tag, so there is no "+N" chip to render — and no cross-links: the collection is fetched
    // with the `simple=1` field set, which carries none, and the SSR template pairs nothing either.
    // Feeding them in here would render an anchor where the server rendered a plain chip.
    const { links } = buildCollectionTagLinks(
        collection.tags,
        NO_CROSS_LINKS,
        collection.tags.length,
    )

    return (
        <CollectionHero
            coverSrc={collection.imageUrl}
            coverAlt={collection.title}
            title={collection.title}
            authorLabel={collection.authorDisplayName}
            authorAvatarSrc={collection.avatarUrl}
            authorHref={getAuthorPageUrl({ authorUsername: collection.authorUsername })}
            description={collection.description}
            descriptionSlot={
                hasClickableLinks(collection.authorUsername) ? (
                    <LinkifiedText text={collection.description} />
                ) : undefined
            }
            descriptionPlaceholder={t(
                isEditor
                    ? 'i18n.collectionDescriptionPlaceholderEditor'
                    : 'i18n.collectionDescriptionPlaceholder',
            )}
            tagsSlot={
                <CollectionTags
                    isUnlisted={!collection.isPublic}
                    typeLabel={COLLECTION_TYPE_LABELS[collection.type]}
                    links={links}
                />
            }
            followSlot={
                isEditor ? null : (
                    <CollectionHeroFollow
                        followersLabel={getNumber(followStatistics.data?.followers_count ?? 0)}
                        followSlot={
                            <FollowButtonContainer
                                username={collection.authorUsername}
                                creatorUserId={collection.authorId}
                                placement="collection_hero"
                                size="tiny"
                            />
                        }
                    />
                )
            }
            actionsSlot={
                <CollectionActionsContainer
                    collection={collection}
                    placement="collection_hero"
                    isEditor={isEditor}
                />
            }
        />
    )
}
